import * as React from "react";
import { fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl } from "../../config";
import { eventCodeBf } from "../../refdata/FilterOptions";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import Fade from "@mui/material/Fade";
import {
  History,
  SimpleTableRow,
  Sort,
  TableHeader,
  OscrLevelTwoRecord,
  KeyValue,
} from "../../interfaces";
import SimpleTableBody from "../MultiHeaderTable/SimpleTableBody";
import { formatDate } from "../../common/services/Utilities";
import {
  formatHistoryRecords,
  sortHistoryRecords,
  getHistoryApiFilters,
} from "../../common/services/HistoryUtilities";
import {
  VIEW_DETAILS,
  BUSINESS_COMBINATIONS,
  BUSINESS_COMBINATIONS_FAILURES,
  PANDAS,
} from "../../refdata/constants";
import InfoIcon from "@mui/icons-material/Info";
import ModalDialog from "../ModalDialog";
import ResultPagination from "../ResultPagination";
import StructureChangeModalDialog from "../StructureChange/StructureChangeModalDialog";
import usePagination from "../../hooks/usePagination";
import { Theme, Typography } from "@mui/material";

import makeStyles from '@mui/styles/makeStyles';

type HistoryApiField = keyof History;

interface Props {
  cert: string;
  ultCert: string;
}

interface Headers {
  title: string;
  apiField: HistoryApiField;
}

interface Metadata {
  count: number;
}

const useClasses = makeStyles((theme: Theme) => ({
  historyTableFootnoteFirstLine: {
    fontSize: 13,
    color: "#666666",
    textAlign: "center",
    lineHeight: ".5rem",
  },
  historyTableFootnoteSecondLine: {
    fontSize: 13,
    color: "#666666",
    textAlign: "center",
    paddingBottom: 16,
  },
}));

export const HeadersMetaData: Headers[] = [
  { title: "Date*", apiField: "EFFDATE" },
  { title: "Description of Event", apiField: "CHANGECODE_DESC_LONG" },
  { title: "View Details", apiField: VIEW_DETAILS },
];

const HistoryHeaders: TableHeader[][] = [
  HeadersMetaData.map((value: Headers) => ({
    title: value.title,
    styles:
      value.apiField === "CHANGECODE_DESC_LONG"
        ? { textAlign: "left" }
        : { textAlign: "center" },
    colSpan: 1,
    apiField: value.apiField === "EFFDATE" ? value.apiField : undefined,
  })),
];

export function formattedHistoryValues(
  apiField: HistoryApiField,
  data: OscrLevelTwoRecord,
) {
  switch (apiField) {
    case "EFFDATE":
      return formatDate(data.EFFDATE);
    case VIEW_DETAILS:
      return <InfoIcon color="primary" />;
    default:
      return data[apiField];
  }
}

export const transformDataToTableBodyData = (
  history: OscrLevelTwoRecord[],
  setDisplayDialog: (val: boolean) => void,
  setBfLevelFour: (data: OscrLevelTwoRecord) => void,
) => {
  const formattedHistory = formatHistoryRecords(history);
  return formattedHistory.map((data: OscrLevelTwoRecord) => {
    const tableCellData: SimpleTableRow = {};
    HeadersMetaData.map((value: Headers) => value.apiField).forEach(
      (apiField: HistoryApiField) => {
        tableCellData[apiField] = {
          value: formattedHistoryValues(apiField, data) as any,
          styles:
            apiField === "CHANGECODE_DESC_LONG" ? { textAlign: "left" } : {},
        };
      },
    );
    tableCellData.rowOnClick = (e: any) => {
      if (e.key === "Enter" || e.nativeEvent.type === "click") {
        setDisplayDialog(true);
        setBfLevelFour(data);
      }
    };
    return tableCellData;
  });
};

export default function BankFindHistory({ cert, ultCert }: Props) {
  const [bfLevelFour, setBfLevelFour] = React.useState(
    {} as OscrLevelTwoRecord,
  );
  const [displayDialog, setDisplayDialog] = React.useState(false);
  const [metadata, setMetaData] = React.useState<Metadata>({
    count: 0,
  });
  const [historyData, setHistoryData] = React.useState<OscrLevelTwoRecord[]>(
    [],
  );

  const [sort, setSort] = React.useState<Sort>({
    sortField: "EFFDATE",
    sortOrder: "ASC",
  });

  const {
    page,
    rowsPerPage,
    handlePageJump,
    handleChangePage,
    handleChangeRowsPerPage,
  } = usePagination(1, 25);

  const [viewBranchesData, setViewBranchesData] = React.useState({
    resultData: [],
    count: 0,
    dataLoaded: false,
    timeout: 0,
  });

  const levelFourEventType = eventCodeBf
    .map((event: any) =>
      event.value.includes(bfLevelFour.REPORT_TYPE) ? event.label : "",
    )
    .filter(eventVal => eventVal !== "")
    .toString();

  function getLevelThreeTitle(eventType: string) {
    const titleLookup: KeyValue = {
      [BUSINESS_COMBINATIONS]: bfLevelFour.SUR_INSTNAME,
      [BUSINESS_COMBINATIONS_FAILURES]: bfLevelFour.SUR_INSTNAME,
      [PANDAS]: bfLevelFour.ACQ_INSTNAME,
    };

    return titleLookup[eventType]
      ? titleLookup[eventType]
      : bfLevelFour.INSTNAME;
  }

  React.useEffect(() => {
    let isMounted = true;
    (async () => {
      const apiFilters = await getHistoryApiFilters(cert, ultCert);

      await fetchData(
        `${ApiBaseUrl()}/history?${apiFilters}&sort_by=${
          sort.sortField
        }&sort_order=${sort.sortOrder.toLowerCase()}&limit=${rowsPerPage}&offset=${(page -
          1) *
          rowsPerPage}`,
      )
        .then(response => {
          if (isMounted) {
            setMetaData(response.totals);
            setHistoryData(sortHistoryRecords(response.data));
          }
        })
        .catch(error => {
          console.error(error);
        });
    })();
    return () => {
      isMounted = false;
    };
  }, [cert, page, rowsPerPage, sort, ultCert]);

  function sortHandler(tableHeader: TableHeader) {
    if (tableHeader.apiField === "EFFDATE") {
      const sortDirection =
        sort.sortField === tableHeader.apiField && sort.sortOrder === "ASC"
          ? "DESC"
          : "ASC";
      setSort({ sortField: tableHeader.apiField, sortOrder: sortDirection });
    }
  }

  function showViewBranchesButton() {
    const viewableEventTypes = [
      "Business Combinations",
      "Business Combinations - Failures",
      "Branch Purchases & Assumptions by Institution",
      "Liquidations",
    ];
    return viewableEventTypes.includes(levelFourEventType);
  }
  const classes = useClasses();
  const branchOfficeCount = bfLevelFour.ACQ_BRANCHES
    ? `${bfLevelFour.ACQ_BRANCHES.toLocaleString()} ${
        bfLevelFour.ACQ_BRANCHES === 1 ? "Branch Office" : "Branch Offices"
      }`
    : "";

  return (
    <div id="bankFindHistory">
      <Typography variant="h4" id="history-event-count">
        {metadata.count.toLocaleString()}{" "}
        {metadata.count === 1 ? "Historical Event" : "Historical Events"}
      </Typography>
      <ResultPagination
        count={metadata.count}
        handleChangePage={handleChangePage as any}
        handlePickPage={handlePageJump}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        limit={rowsPerPage}
        pageNumber={page}
      >
        <MultiHeaderTable
          tableBody={
            <SimpleTableBody
              tableData={transformDataToTableBodyData(
                historyData,
                setDisplayDialog,
                setBfLevelFour,
              )}
            />
          }
          tableHeaders={HistoryHeaders}
          sortProperties={{
            sortDirection: sort.sortOrder,
            sortField: sort.sortField,
            sortHandler,
          }}
        />
        <ModalDialog
          title={getLevelThreeTitle(levelFourEventType)}
          subtitle={
            <>
              {levelFourEventType}{" "}
              <Fade in={viewBranchesData.resultData.length > 0}>
                <Typography color="primary" style={{ display: "inline-block" }}>
                  - {branchOfficeCount}
                </Typography>
              </Fade>
            </>
          }
          display={displayDialog}
          setDisplay={setDisplayDialog}
          onExited={() => {
            setBfLevelFour({} as OscrLevelTwoRecord);
            setViewBranchesData({
              resultData: [],
              dataLoaded: false,
              count: 0,
              timeout: 0,
            });
          }}
        >
          <StructureChangeModalDialog
            dataLoaded={viewBranchesData.dataLoaded}
            timeout={viewBranchesData.timeout}
            showViewBranches={showViewBranchesButton()}
            eventType={levelFourEventType}
            data={bfLevelFour}
            viewBranchesData={viewBranchesData.resultData}
            setViewBranchesData={setViewBranchesData}
          />
        </ModalDialog>
        {historyData.length > 0 ? (
          <>
            <Typography className={classes.historyTableFootnoteFirstLine}>
              *Data prior to 01/01/2000 may include small anomalies which may or
              may not effect the historic events of this institution.
            </Typography>
            <Typography className={classes.historyTableFootnoteSecondLine}>
              If you have questions or concerns, please contact the FDIC using
              the link above.
            </Typography>
          </>
        ) : (
          <></>
        )}
      </ResultPagination>
    </div>
  );
}
